import type { FC } from "react";

import type { UseFormReturn } from "react-hook-form";

import { Select, Text } from "@alpacahq/ui/old";

import type { FinancialProfileForm } from "./schema";

export const Dropdown = ({
  id,
  label,
  value,
  options,
  onChange,
  errorMessage,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string | string[]) => void;
  errorMessage?: string;
  placeholder?: string;
}) => {
  return (
    <div className="mb-8 w-full max-w-60">
      <div className="flex items-center justify-between">
        <label
          htmlFor={id}
          className="block pb-1 font-medium text-base leading-5"
        >
          {label}
        </label>
        {errorMessage && (
          <Text className="text-red-500 text-sm">{errorMessage}</Text>
        )}
      </div>
      <Select
        id={id}
        value={value}
        options={options}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full"
      />
    </div>
  );
};

export const RANGES = {
  "1": { min: 1, max: 24999 },
  "2": { min: 25000, max: 99999 },
  "3": { min: 100000, max: 499999 },
  "4": { min: 500000, max: 999999 },
  "5": { min: 1000000, max: 1000000000 },
};

export const RANGE_OPTIONS = Object.entries(RANGES).map(([key, value]) => ({
  value: key,
  label: `${value.min.toLocaleString()} - ${value.max.toLocaleString()}`,
}));

export const getMinMaxFromValue = (value: string) => {
  if (!Object.hasOwn(RANGES, value)) {
    return { min: 0, max: 0 };
  }
  return RANGES[value as keyof typeof RANGES];
};

export const getValueFromMinMax = ({
  min,
  max,
}: {
  min: number;
  max: number;
}) => {
  min = Number(min);
  max = Number(max);

  const rangeKey = Object.entries(RANGES).find(
    ([, value]) => value.min === min && value.max === max,
  )?.[0];

  return rangeKey || "";
};

type FinancialInputsProps = {
  form: UseFormReturn<FinancialProfileForm>;
};

export const FinancialInputs: FC<FinancialInputsProps> = ({ form }) => {
  const {
    watch,
    formState: { errors },
  } = form;

  const annualIncome = {
    min: watch("annual_income_min") ?? 0,
    max: watch("annual_income_max") ?? 0,
  };

  const liquidNetWorth = {
    min: watch("liquid_net_worth_min") ?? 0,
    max: watch("liquid_net_worth_max") ?? 0,
  };

  const totalNetWorth = {
    min: watch("total_net_worth_min") ?? 0,
    max: watch("total_net_worth_max") ?? 0,
  };

  const onChange =
    (key: "annual_income" | "liquid_net_worth" | "total_net_worth") =>
    (rangeKey: string | string[]) => {
      if (Array.isArray(rangeKey)) {
        rangeKey = rangeKey[0];
      }

      const { min, max } = getMinMaxFromValue(rangeKey);

      form.setValue(`${key}_min`, min);
      form.setValue(`${key}_max`, max);
    };

  return (
    <>
      <div className="flex flex-col md:flex-row md:space-x-4">
        <Dropdown
          id="annual_income"
          label="Annual Income"
          value={getValueFromMinMax(annualIncome)}
          onChange={onChange("annual_income")}
          options={[...RANGE_OPTIONS]}
          errorMessage={errors.annual_income_max?.message}
          placeholder="Select range"
        />
        <Dropdown
          id="total_net_worth"
          label="Total Net Worth"
          value={getValueFromMinMax(totalNetWorth)}
          onChange={onChange("total_net_worth")}
          options={RANGE_OPTIONS}
          errorMessage={errors.total_net_worth_max?.message}
          placeholder="Select range"
        />
      </div>
      <Dropdown
        id="liquid_net_worth"
        label="Liquid Net Worth"
        value={getValueFromMinMax(liquidNetWorth)}
        onChange={onChange("liquid_net_worth")}
        options={RANGE_OPTIONS}
        errorMessage={errors.liquid_net_worth_max?.message}
        placeholder="Select range"
      />
    </>
  );
};
